import { ChangeEvent, FC, useMemo, useState } from 'react';
import {
  Box,
  FormControl,
  FormControlLabel,
  FormLabel,
  List,
  Radio,
  RadioGroup,
  Stack,
  styled,
  Typography,
} from '@mui/material';
import { BuyerIcon, GlyphIcon, Logo, SellerIcon, Sneaker, VersusIcon } from 'assets/img/FeesCalculator';
import { theme } from 'theme';
import { FontWeights } from 'theme/Typography';
import {
  BORDER_RADIUS_LARGE,
  BORDER_RADIUS_SMALL,
  COLOR_BLACK,
  COLOR_DARK_GREEN,
  COLOR_GRAY_100,
  COLOR_GRAY_151,
  COLOR_LIGHT_GREEN,
  COLOR_WHITE,
} from 'theme/variables';

import { CalculatedFees, CalculatedFeesForm } from './components/CalculatedFeesForm';
import { CustomListItem } from './components/CustomListItem';
import { InputField, InputFieldState } from './components/InputField';

const CustomRadioButton = styled(Radio)({
  display: 'none',
});

const StyledFormControlLabel = styled(FormControlLabel)({
  '.MuiFormControlLabel-label': {
    fontFamily: '"Roboto-Mono", sans-serif !important',
    fontSize: 'clamp(0.625rem, 0.4688rem + 0.6944vw, 1.25rem)',
    lineHeight: '1.2em',
    display: 'flex',
    flexDirection: 'row',
    textWrap: 'nowrap',
    alignItems: 'center',
    gap: '8px',
  },
  '.MuiFormControlLabel-label::before': {
    content: '""',
    display: 'block',
    blockSize: '20px',
    inlineSize: '20px',
    borderRadius: BORDER_RADIUS_SMALL,
    backgroundColor: COLOR_WHITE,
  },
  '.Mui-checked + .MuiFormControlLabel-label::before': {
    backgroundColor: COLOR_LIGHT_GREEN,
    backgroundImage: `url(${GlyphIcon})`,
    backgroundSize: '80%',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'center',
  },
});

type GeneralFees = {
  metazFees: CalculatedFees;
  otherFees: CalculatedFees;
};

const emptyFees: CalculatedFees = {
  txnFees: 0,
  processingFees: 0,
  shipping: 0,
  total: 0,
};

export const FeesCalculator: FC = () => {
  const [selectedValue, setSelectedValue] = useState('sell');
  const [inputPrice, setInputPrice] = useState('');
  const [isValid, setIsValid] = useState(true);

  const calculatedFees: GeneralFees = useMemo(() => {
    const price = Number(inputPrice);
    if (price === 0 || !isValid) {
      return { metazFees: { ...emptyFees }, otherFees: { ...emptyFees } };
    }

    const generalFees: GeneralFees =
      selectedValue === 'sell'
        ? {
            metazFees: {
              txnFees: price * 0.03,
              processingFees: 0.01,
              shipping: 0,
              total: 0,
            },
            otherFees: {
              txnFees: price * 0.09,
              processingFees: price * 0.03,
              shipping: 5,
              total: 0,
            },
          }
        : {
            metazFees: {
              txnFees: 0,
              processingFees: 0.01,
              shipping: 0,
              total: 0,
            },
            otherFees: {
              txnFees: 0,
              processingFees: price * 0.09,
              shipping: 5,
              total: 0,
            },
          };

    const metazFeesTotal =
      generalFees.metazFees.txnFees + generalFees.metazFees.processingFees + generalFees.metazFees.shipping;

    const otherFeesTotal =
      generalFees.otherFees.txnFees + generalFees.otherFees.processingFees + generalFees.otherFees.shipping;

    generalFees.metazFees.total = selectedValue === 'sell' ? price - metazFeesTotal : price + metazFeesTotal;
    generalFees.otherFees.total = selectedValue === 'sell' ? price - otherFeesTotal : price + otherFeesTotal;

    return generalFees;
  }, [inputPrice, selectedValue, isValid]);

  const handleChangeOperationType = (event: ChangeEvent<HTMLInputElement>) => {
    setSelectedValue(event.target.value);
  };

  const handleChangePrice = (event: ChangeEvent<HTMLInputElement>) => {
    if (!event.target.value) {
      setInputPrice('');
    } else if (!Number.isNaN(+event.target.value) && /^(?:\d{0,6})(?:[.,]\d{0,2})?$/.test(event.target.value)) {
      setIsValid(+event.target.value >= 50);
      setInputPrice(event.target.value.replace(',', '.'));
    }
  };

  return (
    <Box
      sx={{
        padding: { xs: '10px', md: '50px', lg: '100px' },
        background: COLOR_BLACK,
        borderRadius: { xs: 4, md: 7.5 },
        display: 'grid',
        gap: { xs: '10px', md: 0 },
        gridTemplateColumns: { xs: 'none', md: '1fr 11fr' },
        justifyContent: { xs: 'center', md: 'start' },
        alignItems: { xs: 'center', md: 'start' },
        '& .MuiTypography-root': {
          fontFamily: '"Roboto", sans-serif',
          color: COLOR_WHITE,
        },
      }}
    >
      <Stack
        sx={{ gap: { xs: '20px', sm: '30px', md: '50px', lg: '74px' }, alignItems: { xs: 'center', sm: 'start' } }}
      >
        <Stack>
          <Stack flexDirection="row" sx={{ justifyContent: { xs: 'center', sm: 'start' } }}>
            <Typography
              sx={{
                textShadow: `-1px -1px 0 ${COLOR_WHITE}, 1px -1px 0 ${COLOR_WHITE}, -1px 1px 0 ${COLOR_WHITE}, 1px 1px 0 ${COLOR_WHITE}`,
                fontSize: 'clamp(3rem, 2.25rem + 3.3333vw, 6rem)',
                textTransform: 'uppercase',
                fontWeight: FontWeights.fontWeightBold,
                lineHeight: '85%',
                color: `${COLOR_BLACK} !important`,
              }}
            >
              Earn&nbsp;
            </Typography>
            <Typography
              sx={{
                fontStyle: 'italic',
                fontWeight: FontWeights.fontWeightBlack,
                fontSize: 'clamp(3rem, 2.25rem + 3.3333vw, 6rem)',
                lineHeight: '85%',
              }}
            >
              More
            </Typography>
            <Typography
              sx={{
                fontStyle: 'italic',
                fontWeight: FontWeights.fontWeightLight,
                fontSize: 'clamp(3rem, 2.25rem + 3.3333vw, 6rem)',
                lineHeight: '85%',
              }}
            >
              ,
            </Typography>
          </Stack>
          <Stack flexDirection="row" my="8px" sx={{ justifyContent: { xs: 'center', sm: 'start' } }}>
            <Typography
              sx={{
                textShadow: `-1px -1px 0 ${COLOR_WHITE}, 1px -1px 0 ${COLOR_WHITE}, -1px 1px 0 ${COLOR_WHITE}, 1px 1px 0 ${COLOR_WHITE}`,
                fontSize: 'clamp(3rem, 2.25rem + 3.3333vw, 6rem)',
                textTransform: 'uppercase',
                lineHeight: '85%',
                display: 'block',
                fontWeight: FontWeights.fontWeightLight,
                fontStyle: 'italic',
                color: `${COLOR_BLACK} !important`,
              }}
            >
              Pay&nbsp;
            </Typography>
            <Typography
              sx={{
                fontSize: 'clamp(3rem, 2.25rem + 3.3333vw, 6rem)',
                lineHeight: '85%',
                fontWeight: 100,
                fontStyle: 'italic',
              }}
            >
              Less
            </Typography>
          </Stack>

          <Typography
            sx={{
              fontSize: '14px',
              fontWeight: 200,
              lineHeight: '100%',
              maxWidth: '371px',
              textAlign: { xs: 'center', sm: 'left' },
            }}
          >
            Estimate how much more you earn on METAZ against other resale marketplaces. Let’s go!!
          </Typography>
        </Stack>

        <List sx={{ display: 'flex', flexDirection: 'column', gap: { xs: '20px', md: '70px' } }}>
          <CustomListItem sequenceNumber={1} text="Sell or Buy?">
            <FormControl component="fieldset" sx={{ mx: { xs: 0, lg: '40px' } }}>
              <FormLabel component="legend" sx={{ display: 'none' }}>
                Sell or Buy?
              </FormLabel>
              <RadioGroup
                aria-label="buyOrSell"
                name="buyOrSell"
                value={selectedValue}
                onChange={handleChangeOperationType}
                row
                sx={{
                  gap: '45px',
                  flexWrap: 'nowrap',
                  [theme.breakpoints.down(1550)]: { flexDirection: 'column' },
                  [theme.breakpoints.down(1440)]: { flexDirection: 'row' },
                  [theme.breakpoints.down(1380)]: { flexDirection: 'column' },
                  [theme.breakpoints.down('md')]: { flexDirection: 'row', transform: 'translate(20px, 0)' },
                }}
              >
                <Stack sx={{ alignItems: 'center' }}>
                  <SellerIcon
                    sx={{
                      width: {
                        xs: '60px',
                        sm: '70px',
                        lg: '85px',
                        color: selectedValue === 'sell' ? COLOR_LIGHT_GREEN : COLOR_WHITE,
                      },
                    }}
                  />
                  <StyledFormControlLabel
                    value="sell"
                    control={<CustomRadioButton checked={selectedValue === 'sell'} />}
                    label="I want to sell"
                  />
                </Stack>
                <Stack sx={{ alignItems: 'center' }}>
                  <BuyerIcon
                    sx={{
                      width: {
                        xs: '60px',
                        sm: '70px',
                        lg: '85px',
                        color: selectedValue === 'buy' ? COLOR_LIGHT_GREEN : COLOR_WHITE,
                      },
                    }}
                  />
                  <StyledFormControlLabel
                    value="buy"
                    control={<CustomRadioButton checked={selectedValue === 'buy'} />}
                    label="I want to buy"
                  />
                </Stack>
              </RadioGroup>
            </FormControl>
          </CustomListItem>
          <CustomListItem sequenceNumber={2} text="Enter sneakers price.">
            <Stack sx={{ mx: { xs: 0, lg: '40px' }, flexDirection: 'row', alignItems: 'end', gap: '30px' }}>
              <Box
                component="img"
                src={Sneaker}
                sx={{
                  maxWidth: '142px',
                  transform: 'rotate(4.5deg)',
                }}
              />
              <FormControl component="fieldset" sx={{ gap: '10px', position: 'relative' }}>
                <Typography
                  component="label"
                  htmlFor="sneakers-price"
                  sx={{
                    fontWeight: FontWeights.fontWeightSemiBold,
                    fontSize: 'clamp(1.25rem, 1rem + 1.1111vw, 2.25rem)',
                    textAlign: 'end',
                    lineHeight: '100%',
                    textWrap: { xs: 'initial', lg: 'nowrap' },
                  }}
                >
                  Sneakers price
                </Typography>
                <InputField
                  id="sneakers-price"
                  autoComplete="off"
                  placeholder="0.00"
                  withStartAdornment={{
                    fontWeight: FontWeights.fontWeightBold,
                    fontSize: 'clamp(1.25rem, 1.0625rem + 0.8333vw, 2rem)',
                    color: COLOR_GRAY_151,
                  }}
                  style={{
                    fontSize: 'clamp(1.375rem, 1.125rem + 1.1111vw, 2.375rem)',
                    maxWidth: '283px',
                    borderRadius: BORDER_RADIUS_LARGE,
                    border: '1px solid',
                    borderColor: isValid ? 'transparent' : 'red',
                    color: COLOR_DARK_GREEN,
                  }}
                  onChange={handleChangePrice}
                  value={inputPrice}
                />
                {!isValid && (
                  <Typography textAlign="end" fontSize="12px" position="absolute" right="0" bottom="-25px" noWrap>
                    Enter price more than $50
                  </Typography>
                )}
              </FormControl>
            </Stack>
          </CustomListItem>
        </List>
      </Stack>

      <Stack
        sx={{
          justifyContent: { xs: 'center', md: 'end' },
          alignItems: 'end',
          alignSelf: 'end',
        }}
      >
        <Stack flexDirection="row" justifyContent="end" alignItems="center" sx={{ gap: { xs: '5px', sm: '10px' } }}>
          <CalculatedFeesForm prefixId="metaz" fees={calculatedFees.metazFees}>
            <Logo sx={{ width: { xs: '150px', md: '175px', lg: '215px', color: COLOR_LIGHT_GREEN } }} />
          </CalculatedFeesForm>
          <VersusIcon sx={{ color: COLOR_GRAY_100, width: { xs: '40px', sm: '50px', md: '74px' } }} />
          <CalculatedFeesForm
            prefixId="other-platform"
            active={InputFieldState.DISABLED}
            fees={calculatedFees.otherFees}
          >
            <Typography
              sx={{
                fontWeight: FontWeights.fontWeightMedium,
                fontSize: 'clamp(1rem, 0.6875rem + 1.3889vw, 2.25rem)',
                lineHeight: '52px',
                letterSpacing: '-0.05em',
                color: `${COLOR_GRAY_100} !important`,
                textAlign: 'center',
                textWrap: 'nowrap',
              }}
            >
              Resale Marketplace
            </Typography>
          </CalculatedFeesForm>
        </Stack>
        <Box
          sx={{
            maxWidth: '300px',
            [theme.breakpoints.down(550)]: { maxWidth: '100%' },
            marginBlock: '1px 33px',
          }}
        >
          <Typography
            sx={{
              fontSize: '11px',
              fontWeight: 200,
              lineHeight: '100%',
            }}
          >
            *A fixed shipping rate is applied for a convenience. Shipping cost will vary depending on your area and
            destination. This is only for a simple reference purpose.
          </Typography>
        </Box>
        <Stack
          sx={{
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'end',
            flexWrap: 'wrap',
            ' .MuiTypography-root': {
              fontSize: 'clamp(1.5625rem, 0.7031rem + 3.8194vw, 5rem)',
              textTransform: 'uppercase',
              fontWeight: FontWeights.fontWeightSemiBold,
              lineHeight: '100%',
              fontStyle: 'italic',
            },
          }}
        >
          {selectedValue === 'buy' ? <Typography style={{ textTransform: 'none' }}>You&nbsp;</Typography> : ''}
          <Typography>{selectedValue === 'buy' ? 'Pay' : 'Earn'}&nbsp;</Typography>
          <Typography
            style={{
              color: COLOR_LIGHT_GREEN,
              fontWeight: FontWeights.fontWeightBold,
              fontSize: 'clamp(1.875rem, 0.75rem + 5vw, 6.375rem)',
            }}
          >
            $
            {(selectedValue === 'buy'
              ? Number(calculatedFees.otherFees.total) - Number(calculatedFees.metazFees.total)
              : Number(calculatedFees.metazFees.total) - Number(calculatedFees.otherFees.total)
            ).toFixed(2)}
          </Typography>
          <Typography
            style={{
              color: COLOR_LIGHT_GREEN,
              fontWeight: selectedValue === 'buy' ? FontWeights.fontWeightLight : FontWeights.fontWeightBold,
            }}
          >
            &nbsp;{selectedValue === 'buy' ? 'Less!' : 'More!'}
          </Typography>
        </Stack>
      </Stack>
    </Box>
  );
};
